import type { Family } from './types'
import { FAMILIES } from './families'
import { hashSeed, mulberry32 } from './generator'

// Quêtes journalières: 3 par jour, générées déterministiquement depuis la date
// (les MÊMES pour tous les joueurs — comme les rivaux de la Ligue). Récompense
// en points de rang. Structure la session quotidienne, en synergie avec le
// plafond de minage d'essence.

export const QUEST_REWARD_POINTS = 25

const FAMS: Family[] = ['bitcoin', 'ethereum', 'base', 'defi', 'meme', 'stable']

/** Résumé d'un combat terminé — la matière première du tracking. */
export interface BattleSummary {
  won: boolean
  playerFamily: Family
  enemyFamily: Family
  crits: number
  specials: number
  defends: number
  teamBattle: boolean
  isRival: boolean
  hadDisadvantage: boolean
}

export interface Quest {
  id: string
  text: string
  target: number
  progress: (s: BattleSummary) => number
}

type Template = (rng: () => number) => Quest

const TEMPLATES: Template[] = [
  () => ({
    id: 'win3',
    text: 'Gagne 3 combats',
    target: 3,
    progress: (s) => (s.won ? 1 : 0),
  }),
  (rng) => {
    const f = FAMS[Math.floor(rng() * 6)]
    return {
      id: `winwith-${f}`,
      text: `Gagne un combat avec un monstre ${FAMILIES[f].emoji} ${FAMILIES[f].label}`,
      target: 1,
      progress: (s) => (s.won && s.playerFamily === f ? 1 : 0),
    }
  },
  (rng) => {
    const f = FAMS[Math.floor(rng() * 6)]
    return {
      id: `beat-${f}`,
      text: `Bats un monstre ${FAMILIES[f].emoji} ${FAMILIES[f].label}`,
      target: 1,
      progress: (s) => (s.won && s.enemyFamily === f ? 1 : 0),
    }
  },
  () => ({
    id: 'crit3',
    text: 'Inflige 3 coups critiques',
    target: 3,
    progress: (s) => s.crits,
  }),
  () => ({
    id: 'special5',
    text: 'Lance 5 attaques spéciales',
    target: 5,
    progress: (s) => s.specials,
  }),
  () => ({
    id: 'defend4',
    text: 'Défends 4 fois',
    target: 4,
    progress: (s) => s.defends,
  }),
  () => ({
    id: 'team1',
    text: 'Gagne un combat en 3v3',
    target: 1,
    progress: (s) => (s.won && s.teamBattle ? 1 : 0),
  }),
  () => ({
    id: 'rival1',
    text: 'Bats un rival de la Ligue',
    target: 1,
    progress: (s) => (s.won && s.isRival ? 1 : 0),
  }),
  () => ({
    id: 'upset1',
    text: 'Gagne malgré un désavantage de famille',
    target: 1,
    progress: (s) => (s.won && s.hadDisadvantage ? 1 : 0),
  }),
]

export function todayKey(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Les 3 quêtes du jour — identiques pour tous (seed = la date). */
export function dailyQuests(day: string = todayKey()): Quest[] {
  const rng = mulberry32(hashSeed('cryptoket-quests:' + day))
  const picks: number[] = []
  while (picks.length < 3) {
    const i = Math.floor(rng() * TEMPLATES.length)
    if (!picks.includes(i)) picks.push(i)
  }
  return picks.map((i) => TEMPLATES[i](rng))
}

// ---------------------------------------------------------------------------
// État local: progression + quêtes accomplies, remis à zéro chaque jour.
// ---------------------------------------------------------------------------

const KEY_QUESTS = 'cryptoket.quests'

interface QuestState {
  day: string
  progress: Record<string, number>
  done: string[]
}

function loadState(): QuestState {
  const day = todayKey()
  try {
    const s = JSON.parse(localStorage.getItem(KEY_QUESTS) ?? '{}') as Partial<QuestState>
    if (s.day === day) return { day, progress: s.progress ?? {}, done: s.done ?? [] }
  } catch {
    // état corrompu → journée vierge
  }
  return { day, progress: {}, done: [] }
}

function saveState(s: QuestState): void {
  localStorage.setItem(KEY_QUESTS, JSON.stringify(s))
}

export interface QuestStatus {
  quest: Quest
  progress: number
  done: boolean
}

export function questStatuses(): QuestStatus[] {
  const state = loadState()
  return dailyQuests(state.day).map((quest) => ({
    quest,
    progress: Math.min(state.progress[quest.id] ?? 0, quest.target),
    done: state.done.includes(quest.id),
  }))
}

/** Met à jour la progression après un combat. Renvoie les quêtes accomplies à l'instant. */
export function reportBattle(summary: BattleSummary): Quest[] {
  const state = loadState()
  const completed: Quest[] = []
  for (const quest of dailyQuests(state.day)) {
    if (state.done.includes(quest.id)) continue
    const inc = quest.progress(summary)
    if (inc <= 0) continue
    const next = (state.progress[quest.id] ?? 0) + inc
    state.progress[quest.id] = next
    if (next >= quest.target) {
      state.done.push(quest.id)
      completed.push(quest)
    }
  }
  saveState(state)
  return completed
}
